import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import { DispatchError, type DispatchRepository } from '../application/DispatchRepository'
import {
  dispatchHistorySchema,
  dispatchProposalSchema,
  dispatchSettingsSchema,
  dispatchWorkspaceSchema,
  draftWriteResultSchema,
  publicationPreviewSchema,
  publishResultSchema,
  roadBudgetSchema,
  roadTimesEstimateSchema,
  roadTimesResultSchema,
  versionContentSchema,
} from '../domain/dispatch'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Code d'erreur stable (`DISPATCH_DRAFT_CONFLICT`, …), jamais le message brut. */
function rpcErrorCode(message: string): string {
  return /(DISPATCH|ROUTE_SECTOR|PAID_API)_[A-Z_]+/.exec(message)?.[0] ?? 'UNEXPECTED'
}

/** Code d'erreur renvoyé par la fonction Edge (`{ error }`), jamais le corps brut. */
async function functionErrorCode(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (context instanceof Response) {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null
    if (typeof body?.error === 'string' && /^[A-Z0-9_]{1,80}$/.test(body.error)) return body.error
  }
  return 'UNEXPECTED'
}

/** Aucun repli sur des fixtures : un faux plan se lirait comme la répartition réelle. */
export function createSupabaseDispatchRepository(client: AppSupabaseClient): DispatchRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function rpc<T>(name: string, command: object, schema: z.ZodType<T>): Promise<T> {
    let response: RpcResponse
    try {
      response = await rpcClient.rpc(name, { p_command: command })
    } catch {
      throw new DispatchError('NETWORK')
    }
    if (response.error) throw new DispatchError(rpcErrorCode(response.error.message))
    const parsed = schema.safeParse(response.data)
    if (!parsed.success) throw new DispatchError('RESPONSE_INVALID')
    return parsed.data
  }

  /** Fonction Edge `route-optimization` (actions de la répartition). */
  async function invoke<T>(body: object, schema: z.ZodType<T>): Promise<T> {
    let invocation: Awaited<ReturnType<typeof client.functions.invoke>>
    try {
      invocation = await client.functions.invoke('route-optimization', { body })
    } catch {
      throw new DispatchError('NETWORK')
    }
    if (invocation.error) throw new DispatchError(await functionErrorCode(invocation.error))
    const parsed = schema.safeParse(invocation.data)
    if (!parsed.success) throw new DispatchError('RESPONSE_INVALID')
    return parsed.data
  }

  return {
    getWorkspace: (season) => rpc('dispatch_plan_workspace_v1', { season }, dispatchWorkspaceSchema),
    saveDraft: (command) => rpc('dispatch_plan_save_draft_v1', command, draftWriteResultSchema),
    resetDraft: (command) => rpc('dispatch_plan_reset_draft_v1', command, draftWriteResultSchema),
    restoreVersion: (command) => rpc('dispatch_plan_restore_version_v1', command, draftWriteResultSchema),
    getPublicationPreview: (season) =>
      rpc('dispatch_plan_publication_preview_v1', { season }, publicationPreviewSchema),
    publish: (command) => rpc('dispatch_plan_publish_v1', command, publishResultSchema),
    async applyPending(season) {
      await rpc('dispatch_plan_apply_pending_v1', { season }, z.unknown())
    },
    getHistory: (season) => rpc('dispatch_plan_history_v1', { season }, dispatchHistorySchema),
    getVersion: (versionId) => rpc('dispatch_plan_version_v1', { versionId }, versionContentSchema),
    updateSettings: (command) => rpc('dispatch_settings_update_v1', command, dispatchSettingsSchema),
    async setRouteSector(routeId, sectorId) {
      await rpc(
        'set_route_sector_v1',
        { idempotencyKey: crypto.randomUUID(), routeId, sectorId },
        z.unknown(),
      )
    },
    getRoadBudget: () => rpc('paid_api_budget_v1', { budget: 'DISPATCH_ROAD_TIMES' }, roadBudgetSchema),
    setRoadBudget: (monthlyUnitCap) =>
      rpc('paid_api_budget_set_v1', { budget: 'DISPATCH_ROAD_TIMES', monthlyUnitCap }, roadBudgetSchema),
    estimateRoadTimes: (season, scope) =>
      rpc('dispatch_road_times_begin_v1', { season, scope, dryRun: true }, roadTimesEstimateSchema),
    computeRoadTimes: (season) =>
      invoke(
        { action: 'dispatchRoadTimes', idempotencyKey: crypto.randomUUID(), season, scope: 'PLAN' },
        roadTimesResultSchema,
      ),
    optimize: ({ season, useRoadTimes }) =>
      invoke(
        {
          action: 'dispatchPlan',
          idempotencyKey: crypto.randomUUID(),
          roadTimesKey: crypto.randomUUID(),
          season,
          useRoadTimes,
        },
        dispatchProposalSchema,
      ),
    getProposal: (season) =>
      rpc('dispatch_optimization_run_v1', { season }, dispatchProposalSchema.nullable()),
    applyProposal: (runId) => rpc('dispatch_optimization_apply_v1', { runId }, dispatchProposalSchema),
    dismissProposal: (runId) => rpc('dispatch_optimization_dismiss_v1', { runId }, dispatchProposalSchema),
  }
}
